import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ITEM_REVENUE_CATEGORY } from 'src/app/helpers/item-revenue.helper';
import { ORDER_CATEGORY } from 'src/app/helpers/order-categories.helper';
import Utils from 'src/app/helpers/utils.helper';
import { CartItems } from 'src/app/models/cart-items.model';
import { CompanyShoes } from 'src/app/models/company-shoes.model';
import { ShoesFilter } from 'src/app/models/filters/shoe-filter.model';
import { CartItemsRequest } from 'src/app/models/requests/cart-items-request.model';
import { SelectedShoe } from 'src/app/models/local-storage/selected-shoe.model';
import { ShoeModel } from 'src/app/models/shoe-model.model';
import { CartManagerService } from 'src/app/services/local/cart-manager.service';
import { LocalStorageService } from 'src/app/services/local/local-storage.service';
import { CompanyShoesService } from 'src/app/services/repositories/company-shoes.service';
import { CompanyShoeInsolesService } from 'src/app/services/repositories/company-shoe-insoles.service';

@Component({
    selector: 'app-safety-details-options',
    templateUrl: './safety-shoes-details.component.html',
    styleUrls: ['./safety-shoes-details.component.scss'],
    standalone: false
})
export class SafetyShoesDetailsComponent implements OnInit {
  toggleClass: boolean = true;
  shoeStyleRefId: number = 0;
  shoe: CompanyShoes | undefined;
  queryParams: ParamMap | undefined;
  shoeProperties: string[] = [];
  trustPilotDataSkus: any;
  currentSku: number | undefined;
  shoeWidths: { value: string; isAvailable: boolean }[]  = []
  shoeSizes: { value: string; isAvailable: boolean }[] = []
  selectedShoeSize: string | undefined;
  selectedShoeWidth: string | undefined;
  selectedShoeSizeIndex: number | undefined;
  selectedShoeWidthIndex: number | undefined;
  isEditMode: boolean = false;
  selectedShoe: SelectedShoe | undefined;
  shoeImages: string[] = [];

  private readonly N_ADD_TO_CART_WARN_TITLE = 'Add to Cart';
  private readonly N_ADD_TO_CART_WARN_MSG = 'You must add a shoe size and shoe width.';

  private hasShoeAccessories: boolean = false;
  get hasAttributes(): boolean {
    return !!this.shoe?.attributes && Object.keys(this.shoe?.attributes).length > 0;
  }

  constructor(private _cartManagerService: CartManagerService,
              private _companyShoesService: CompanyShoesService,
              private _route: ActivatedRoute,
              private _notificationService: ToastrService,
              private _localStorageService: LocalStorageService,
              private _companyShoeInsolesService: CompanyShoeInsolesService,
              private _router: Router) { }

  ngOnInit(): void {
    this._route.queryParams
      .subscribe(params => {
        this.isEditMode = params.isEditMode === 'true';
        this.selectedShoe = this._localStorageService.getSelectedStyle();
        if (this.selectedShoe != null) {
          this.getShoeDetails();
          // for now we are doing this for insoles as a test
          this.checkShoeAccessories();
        }
        else {
          this._router.navigate(['/products/safety-shoes']);
        }
      });
  }

  checkShoeAccessories() {
    this._companyShoeInsolesService.getShoeInsoles()
    .subscribe((data: any) => {
      if (data.items.length > 0) {
        this.hasShoeAccessories = true;
      }
    }, (error: any) => {
    });
  }

  setSkusForReview() {
    this.trustPilotDataSkus = [ this.shoe?.itemId ];
  }

  getShoeDetails() {
    if (this.selectedShoe) {
      let filters = this.getFilters();
      let filterParams = new HttpParams();
      if (filters) {
        filterParams = Utils.getHttpParams(filters, filterParams);
      }
      this._companyShoesService.getShoeDetails(this.selectedShoe.companyShoeId, filterParams)
      .subscribe((data: CompanyShoes) => {
        if (data) {
          this.shoe = data;
          this.setShoeDetails();
          this.setSkusForReview();
        }
      }, (error: any) => {
        console.log(error);
      });
    }
  }

  getFilters() : ShoesFilter {
    let filters = new ShoesFilter();
    if (!this.isEditMode) {
      // dont touch persisted filters
      filters = this._localStorageService.getProductFilters() as ShoesFilter;
    }
    else {
      // edit mode will use its own filter
      if (this.selectedShoe) {
        if (this.selectedShoe.gender) {
          filters.gender = this.selectedShoe.gender;
        }
      }
    }
    return filters;
  }

  setShoeDetails() {
    if (this.shoe) {
      this.setSizes(this.shoe);
      this.setWidths(this.shoe);
      this.shoeProperties = this.shoe.shoeProperties.join(', ');
      this.currentSku = this.shoe.itemId;
      this.setImages();
      if (this.selectedShoe) {
        this.setSelectedOptions();
      }
    }
  }

  setImages() {
    if (this.shoe) {
      Object.entries(this.shoe.shoeImages)
      .forEach(([key, value]) =>  {
        if (value) {
          this.shoeImages.push(value);
        }
      })
    }
  }

  setSelectedOptions() {
    this.selectedShoeSize = this.selectedShoe?.size;
    this.selectedShoeWidth = this.selectedShoe?.width;
    this.selectedShoeSizeIndex = this.shoeSizes.findIndex(s => s.value == this.selectedShoe?.size);
    this.selectedShoeWidthIndex = this.shoeWidths.findIndex(s => s.value == this.selectedShoe?.width);
  }

  setWidths(shoe: CompanyShoes, size?: string) {
    let uniqueWidths: string[] = [];
    let shoeModels: ShoeModel[] = shoe.shoeModels;

    this.shoeWidths = [];

    if (size) {
      shoeModels = shoe.shoeModels.filter(sm => sm.size == size);
    }

    uniqueWidths = [...new Set(shoeModels.map(sm => sm.width)
      .sort((a, b) => (a > b ? -1 : 1)))];

    uniqueWidths.forEach(width => {
      const availableShoeModels = shoeModels.filter(sm => sm.width == width && sm.isAvailable == true);
      const isAvailable = availableShoeModels.length > 0 ? true : false;
      this.shoeWidths?.push({value: width, isAvailable: isAvailable })
    });
  }

  setSizes(shoe: CompanyShoes, width?: string) {
    let uniqueSizes: string[] = [];
    let shoeModels: ShoeModel[] = shoe.shoeModels;

    this.shoeSizes = [];

    if (width) {
      shoeModels = shoe.shoeModels.filter(sm => sm.width == width);
    }

    uniqueSizes = [...new Set( shoeModels.map(sm => sm.size)
      .sort((a,b) => (parseFloat(a) - parseFloat(b))))];

    uniqueSizes.forEach(size => {
      const availableShoeModels = shoeModels.filter(sm => sm.size == size && sm.isAvailable == true);
      const isAvailable = availableShoeModels.length > 0 ? true : false;
      this.shoeSizes?.push({value: size, isAvailable: isAvailable})
    });
  }

  onSizeChange(val: string) {
    if (this.shoe) {
      this.selectedShoeSize = val;
     this.selectedShoeSizeIndex = this.shoeSizes.findIndex(s => s.value == val);
     this.setWidths(this.shoe, val);
    }
  }

  onWidthChange(val: string) {
    if (this.shoe) {
      this.selectedShoeWidth = val;
      this.selectedShoeWidthIndex = this.shoeWidths.findIndex(s => s.value == val);
      this.setSizes(this.shoe, val);
     }
  }

  saveShoeSelection() {
    if (this.shoe) {
      if (this.selectedShoeSize && this.selectedShoeWidth) {
        let selectedShoeModel = this.shoe.shoeModels.find(sm => sm.size == this.selectedShoeSize && sm.width == this.selectedShoeWidth);
        if (selectedShoeModel) {
          let shoeDescription = `${this.shoe.brand} ${this.shoe.style} (${this.shoe.color}) - ${this.shoe.shoeModels[0].gender} Size: ${this.selectedShoeSize} Width: ${this.selectedShoeWidth}`
          const cartItem : CartItemsRequest = {
            itemCategory: ORDER_CATEGORY.shoes,
            itemDescription: shoeDescription,
            itemImageName: this.shoe.shoeImages.imageMain,
            sku: selectedShoeModel.sku,
            itemDetails: [{
                            id: this.shoe.id,
                            itemType: ITEM_REVENUE_CATEGORY.shoes,
                            itemDescription: shoeDescription
                          }]
          };
          this._cartManagerService.clearTempCartItem();
          this._cartManagerService.saveTempCartItem(cartItem);
        }
        else {
          this.showCartAddError();
        }
      }
      else {
        this.showCartAddError();
      }
    }
  }

  showCartAddError() {
    this._notificationService.warning(this.N_ADD_TO_CART_WARN_MSG, this.N_ADD_TO_CART_WARN_TITLE);
  }

  addToCart() {
    this.saveShoeSelection();
    const tempCartItem = this._cartManagerService.getTempCartItem();
    if (tempCartItem) {
      if (!this.isEditMode) {
        this._cartManagerService.addCartItem(tempCartItem)
        .subscribe((newCartItem: CartItems) => {
            if (newCartItem) {
              this._cartManagerService.saveNewCartItemRef(newCartItem);
            }
            if (!this.hasShoeAccessories) {
              this._router.navigate(['cart']);
            }
            else {
              this._router.navigate(['products/shoe-insoles']);
            }

          },
          (error: any) => {
            console.log(error);
          });
      }
      else {
        const editItem = this._cartManagerService.getEditItem();
        if (editItem) {
          this._cartManagerService.updateCartItem(tempCartItem, editItem.itemId)
          .subscribe((newCartItem: CartItems) => {
            // accessories are not editable right now - LM
              this._router.navigate(['cart']);
            },
            (error: any) => {
              console.log(error);
            });
        }
      }
    }
  }

}
